import type { TicketDto } from '@aawas/contracts';
import { Camera, Clock, Plus, Send, Star, Ticket, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, fetchBlobUrl } from '../lib/api';
import { uploadAttachment, useAddComment, useCategories, useMembers, useTicket, useTicketAction, useTickets, useUpdateTicket } from '../lib/queries';
import { usePollMs } from '../lib/realtime';
import { isResident, useSession } from '../lib/session';
import { Badge, Button, Card, Empty, ErrorNote, Field, Input, PageHeader, Segmented, Select, Spinner, StatusBadge, TICKET_TONE, Textarea, fmtDateTime, timeAgo } from '../ui';
import { useToast } from '../ui/toast';

const PRIORITY_TONE = { low: 'gray', normal: 'blue', high: 'amber', urgent: 'red' } as const;

export function TicketRow({ t, showUnit }: { t: TicketDto; showUnit?: boolean }) {
  return (
    <Link to={`/complaints/${t.id}`}>
      <Card className="!p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate font-semibold">#{t.number} {t.title}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
              {showUnit && <span>{t.unitLabel ? `Flat ${t.unitLabel}` : 'Common area'}</span>}
              {t.categoryName && <span>{t.categoryName}</span>}
              <span>{timeAgo(t.createdAt)}</span>
              {t.assignedToName && <span>→ {t.assignedToName}</span>}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <StatusBadge map={TICKET_TONE} status={t.status} />
            {t.slaBreached && <Badge tone="red"><Clock className="mr-1 h-3 w-3" />Overdue</Badge>}
            {t.priority !== 'normal' && <Badge tone={PRIORITY_TONE[t.priority]}>{t.priority}</Badge>}
          </div>
        </div>
      </Card>
    </Link>
  );
}

export function ComplaintsScreen() {
  const { current } = useSession();
  const poll = usePollMs(60_000, 15_000);
  const staffView = current.role === 'admin' || current.role === 'staff';
  const [tab, setTab] = useState<'open' | 'done'>('open');
  const [mineOnly, setMineOnly] = useState(false);
  const q = useTickets({ limit: 100, ...(mineOnly && { assignedToMe: true }) }, poll);
  const all = q.data?.items ?? [];
  const isOpen = (t: TicketDto) => t.status !== 'closed' && t.status !== 'resolved';
  const items = all.filter((t) => (tab === 'open' ? isOpen(t) : !isOpen(t)));
  const overdue = all.filter((t) => t.slaBreached).length;

  return (
    <div className="space-y-4">
      <PageHeader
        title={staffView ? 'Complaints' : 'My complaints'}
        subtitle={staffView && overdue ? `${overdue} overdue` : undefined}
        action={isResident(current.role) || current.role === 'admin' ? <Link to="/complaints/new"><Button size="sm"><Plus className="h-4 w-4" /> New</Button></Link> : undefined}
      />
      <Segmented value={tab} onChange={setTab} options={[{ value: 'open', label: 'Open', badge: all.filter(isOpen).length || undefined }, { value: 'done', label: 'Resolved' }]} />
      {current.role === 'staff' && (
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} /> Only assigned to me</label>
      )}
      {q.isLoading ? <Spinner /> : q.error ? <ErrorNote error={q.error} retry={() => q.refetch()} /> : items.length === 0 ? (
        <Empty icon={<Ticket className="h-8 w-8" />} title={tab === 'open' ? 'Nothing open' : 'Nothing here yet'} hint={tab === 'open' && !staffView ? 'Something not working? Report it and the committee will follow up.' : undefined} />
      ) : (
        <div className="space-y-2">{items.map((t) => <TicketRow key={t.id} t={t} showUnit={staffView} />)}</div>
      )}
    </div>
  );
}

// ───────────── new complaint ─────────────

export function NewComplaintScreen() {
  const { current } = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const cats = useCategories();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [priority, setPriority] = useState('normal');
  const [common, setCommon] = useState(!current.unitId);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach(URL.revokeObjectURL), [previews]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const t = await api<TicketDto>('/tickets', {
        body: { title: title.trim(), description: description.trim(), priority, ...(categoryId && { categoryId }), ...(!common && current.unitId && { unitId: current.unitId }) },
      });
      const failed: string[] = [];
      for (const f of files) {
        try { await uploadAttachment(t.id, f); } catch { failed.push(f.name); }
      }
      if (failed.length) toast.show({ title: 'Complaint sent, but some photos failed', body: failed.join(', '), tone: 'alert' });
      else toast.success(`Complaint #${t.number} sent`);
      nav(`/complaints/${t.id}`, { replace: true });
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <PageHeader title="Report a problem" />
      <Field label="What is the problem?"><Input value={title} onChange={(e) => setTitle(e.target.value)} required minLength={3} maxLength={140} autoFocus placeholder="e.g. Kitchen tap is leaking" /></Field>
      <Field label="Details"><Textarea value={description} onChange={(e) => setDescription(e.target.value)} required minLength={3} maxLength={4000} placeholder="When did it start? Where exactly?" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Category">
          <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">Choose…</option>
            {cats.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </Field>
        <Field label="How urgent?">
          <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
            <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
          </Select>
        </Field>
      </div>
      {current.unitId && (
        <label className="flex items-center gap-3 rounded-xl bg-white p-3 ring-1 ring-slate-200">
          <input type="checkbox" className="h-5 w-5" checked={common} onChange={(e) => setCommon(e.target.checked)} />
          <span className="text-sm"><b>Common area</b> — lift, parking, garden, corridor (not inside my flat)</span>
        </label>
      )}
      <div>
        <div className="mb-1.5 text-sm font-medium">Photos <span className="font-normal text-slate-500">(optional, up to 5)</span></div>
        <div className="flex flex-wrap gap-2">
          {previews.map((src, i) => (
            <div key={src} className="relative h-20 w-20 overflow-hidden rounded-xl ring-1 ring-slate-300">
              <img src={src} alt={`Attachment ${i + 1}`} className="h-full w-full object-cover" />
              <button type="button" aria-label="Remove photo" className="absolute right-1 top-1 rounded-full bg-slate-900/70 p-0.5 text-white" onClick={() => setFiles(files.filter((_, j) => j !== i))}><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {files.length < 5 && (
            <button type="button" onClick={() => fileRef.current?.click()} className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-300 text-xs text-slate-600 hover:bg-white">
              <Camera className="h-6 w-6" aria-hidden /> Add
            </button>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" multiple hidden onChange={(e) => { setFiles([...files, ...Array.from(e.target.files ?? [])].slice(0, 5)); e.target.value = ''; }} />
      </div>
      <Button type="submit" size="lg" className="w-full" loading={busy} disabled={title.trim().length < 3 || description.trim().length < 3}>Send complaint</Button>
    </form>
  );
}

// ───────────── detail ─────────────

function Photo({ id }: { id: string }) {
  const [src, setSrc] = useState<string>();
  const [big, setBig] = useState(false);
  useEffect(() => {
    let url: string | undefined;
    let cancelled = false;
    fetchBlobUrl(`/attachments/${id}`).then((u) => { url = u; if (!cancelled) setSrc(u); }).catch(() => undefined);
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [id]);
  if (!src) return <div className="h-24 w-24 animate-pulse rounded-xl bg-slate-200" />;
  return (
    <>
      <button onClick={() => setBig(true)} className="h-24 w-24 overflow-hidden rounded-xl ring-1 ring-slate-300"><img src={src} alt="Complaint photo" className="h-full w-full object-cover" /></button>
      {big && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" onClick={() => setBig(false)} role="dialog" aria-label="Photo">
          <img src={src} alt="Complaint photo, enlarged" className="max-h-full max-w-full rounded-xl" />
        </div>
      )}
    </>
  );
}

export function ComplaintDetailScreen() {
  const { id = '' } = useParams();
  const { current, user } = useSession();
  const toast = useToast();
  const poll = usePollMs(60_000, 10_000);
  const q = useTicket(id, poll);
  const comment = useAddComment();
  const update = useUpdateTicket();
  const action = useTicketAction();
  const staff = useMembers('staff', current.role === 'admin');
  const admins = useMembers('admin', current.role === 'admin');
  const [text, setText] = useState('');
  const [internal, setInternal] = useState(false);
  const [rating, setRating] = useState(0);
  const isStaffSide = current.role === 'admin' || current.role === 'staff';
  const t = q.data;

  const assignable = useMemo(() => {
    const seen = new Map<string, string>();
    for (const m of [...(staff.data ?? []), ...(admins.data ?? [])]) seen.set(m.userId, m.name ?? m.phone);
    return [...seen.entries()];
  }, [staff.data, admins.data]);

  if (q.isLoading) return <Spinner />;
  if (q.error || !t) return <ErrorNote error={q.error ?? new Error('Complaint not found')} retry={() => q.refetch()} />;

  const isCreator = t.createdById === user.id;
  const patch = (p: Record<string, unknown>, msg?: string) => update.mutate({ id, patch: p }, { onError: (e) => toast.error(e), onSuccess: () => msg && toast.success(msg) });

  async function send(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await comment.mutateAsync({ id, body: text.trim(), internal });
      setText('');
    } catch (err) { toast.error(err); }
  }

  const NEXT: Record<string, Array<[string, string]>> = {
    open: [['acknowledged', 'Acknowledge'], ['in_progress', 'Start work'], ['resolved', 'Mark resolved']],
    reopened: [['acknowledged', 'Acknowledge'], ['in_progress', 'Start work'], ['resolved', 'Mark resolved']],
    acknowledged: [['in_progress', 'Start work'], ['resolved', 'Mark resolved']],
    in_progress: [['resolved', 'Mark resolved']],
  };

  return (
    <div className="space-y-4">
      <PageHeader title={`#${t.number} ${t.title}`} subtitle={`${t.unitLabel ? `Flat ${t.unitLabel}` : 'Common area'} · ${t.createdByName ?? 'Resident'} · ${fmtDateTime(t.createdAt)}`} />
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge map={TICKET_TONE} status={t.status} />
        {t.priority !== 'normal' && <Badge tone={PRIORITY_TONE[t.priority]}>{t.priority} priority</Badge>}
        {t.categoryName && <Badge>{t.categoryName}</Badge>}
        {t.slaBreached && <Badge tone="red">Overdue</Badge>}
        {t.slaDueAt && !t.slaBreached && t.status !== 'resolved' && t.status !== 'closed' && <Badge tone="gray">Target {fmtDateTime(t.slaDueAt)}</Badge>}
      </div>

      <Card>
        <p className="whitespace-pre-wrap text-slate-800">{t.description}</p>
        {t.attachmentIds.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{t.attachmentIds.map((a) => <Photo key={a} id={a} />)}</div>}
        {t.assignedToName && <p className="mt-3 text-sm text-slate-600">Assigned to <b>{t.assignedToName}</b></p>}
      </Card>

      {isStaffSide && t.status !== 'closed' && (
        <Card className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {(NEXT[t.status] ?? []).map(([s, label]) => <Button key={s} size="sm" variant={s === 'resolved' ? 'success' : 'secondary'} loading={update.isPending} onClick={() => patch({ status: s }, { acknowledged: 'Acknowledged', in_progress: 'Work started', resolved: 'Marked as resolved' }[s] ?? 'Updated')}>{label}</Button>)}
          </div>
          {current.role === 'admin' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Assign to">
                <Select value={t.assignedToId ?? ''} onChange={(e) => patch({ assignedTo: e.target.value || null }, 'Assignment updated')}>
                  <option value="">Unassigned</option>
                  {assignable.map(([uid, name]) => <option key={uid} value={uid}>{name}</option>)}
                </Select>
              </Field>
              <Field label="Priority">
                <Select value={t.priority} onChange={(e) => patch({ priority: e.target.value })}>
                  <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
                </Select>
              </Field>
            </div>
          )}
        </Card>
      )}

      {isCreator && t.status === 'resolved' && (
        <Card className="space-y-3 bg-emerald-50 ring-emerald-200">
          <div className="font-semibold text-emerald-900">Has this been fixed?</div>
          <div className="flex gap-1" role="radiogroup" aria-label="Rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} role="radio" aria-checked={rating === n} aria-label={`${n} star${n > 1 ? 's' : ''}`} onClick={() => setRating(n)} className="p-1"><Star className={`h-8 w-8 ${n <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} /></button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button variant="secondary" onClick={() => { const reason = window.prompt('What is still wrong?'); if (reason && reason.trim().length >= 3) action.mutate({ id, action: 'reopen', reason: reason.trim() }, { onError: (e) => toast.error(e) }); }}>Not fixed</Button>
            <Button variant="success" loading={action.isPending} onClick={() => action.mutate({ id, action: 'close', ...(rating && { rating }) }, { onError: (e) => toast.error(e), onSuccess: () => toast.success('Thanks for the feedback') })}>Yes, close it</Button>
          </div>
        </Card>
      )}
      {isCreator && t.status === 'closed' && t.rating && <p className="text-sm text-slate-600">You rated this {t.rating}/5.</p>}
      {isCreator && (t.status === 'closed') && (
        <Button variant="secondary" onClick={() => { const reason = window.prompt('Why are you reopening this?'); if (reason && reason.trim().length >= 3) action.mutate({ id, action: 'reopen', reason: reason.trim() }, { onError: (e) => toast.error(e) }); }}>Reopen</Button>
      )}

      <section aria-label="Conversation" className="space-y-2">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600">Updates</h2>
        {t.comments.length === 0 && <p className="text-sm text-slate-500">No replies yet.</p>}
        {t.comments.map((c) => (
          <div key={c.id} className={`rounded-2xl p-3 text-sm ${c.internal ? 'bg-amber-50 ring-1 ring-amber-200' : c.authorId === user.id ? 'ml-8 bg-brand-50' : 'mr-8 bg-white ring-1 ring-slate-200'}`}>
            <div className="mb-0.5 flex items-center justify-between gap-2 text-xs text-slate-500">
              <span className="font-semibold text-slate-700">{c.authorName ?? 'Someone'}{c.internal ? ' · internal note' : ''}</span>
              <span>{timeAgo(c.createdAt)}</span>
            </div>
            <p className="whitespace-pre-wrap">{c.body}</p>
          </div>
        ))}
        {t.status !== 'closed' && (
          <form onSubmit={send} className="space-y-2 pt-1">
            <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={internal ? 'Internal note (residents cannot see this)' : 'Write a reply…'} maxLength={2000} aria-label="Reply" />
            <div className="flex items-center justify-between gap-3">
              {isStaffSide ? <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4" checked={internal} onChange={(e) => setInternal(e.target.checked)} /> Internal note</label> : <span />}
              <Button type="submit" loading={comment.isPending} disabled={!text.trim()}><Send className="h-4 w-4" /> Send</Button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
